/**
 * Calculates geographic bearing in degrees (0-360) from point a [lng, lat] to point b [lng, lat]
 */
export const computeBearing = (a, b) => {
  if (!a || !b) return 0;
  let pA = a;
  let pB = b;
  // Automatically normalize if [lat, lng] was passed instead of [lng, lat]
  if (Array.isArray(pA) && pA.length >= 2 && pA[0] < 45 && pA[1] > 55) {
    pA = [pA[1], pA[0]];
  }
  if (Array.isArray(pB) && pB.length >= 2 && pB[0] < 45 && pB[1] > 55) {
    pB = [pB[1], pB[0]];
  }
  if (pA[0] === pB[0] && pA[1] === pB[1]) return 0;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const y = Math.sin(toRad(pB[0] - pA[0])) * Math.cos(toRad(pB[1]));
  const x =
    Math.cos(toRad(pA[1])) * Math.sin(toRad(pB[1])) -
    Math.sin(toRad(pA[1])) * Math.cos(toRad(pB[1])) * Math.cos(toRad(pB[0] - pA[0]));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

/**
 * Calculates the road angle (bearing) along a route polyline closest to coordinate p [lng, lat]
 */
export const getRouteHeading = (p, polyline) => {
  if (!polyline || polyline.length < 2) return 0;
  let coord = p;
  if (coord && Array.isArray(coord) && coord.length >= 2 && coord[0] < 45 && coord[1] > 55) {
    coord = [coord[1], coord[0]];
  }
  if (!coord || !Number.isFinite(coord[0]) || !Number.isFinite(coord[1])) {
    return computeBearing(polyline[0], polyline[1]);
  }
  let minDist = Infinity;
  let bestIdx = 0;
  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];
    const midLng = (a[0] + b[0]) / 2;
    const midLat = (a[1] + b[1]) / 2;
    const d = (coord[0] - midLng) ** 2 + (coord[1] - midLat) ** 2;
    if (d < minDist) {
      minDist = d;
      bestIdx = i;
    }
  }
  return computeBearing(polyline[bestIdx], polyline[bestIdx + 1]);
};

/**
 * Decodes Google Encoded Polyline into GeoJSON coordinate pairs [[lng, lat], ...]
 */
export const decodePolyline = (encoded = '') => {
  if (!encoded || typeof encoded !== 'string') return [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  const path = [];

  while (index < encoded.length) {
    let shift = 0;
    let result = 0;
    let byte = null;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);

    lat += result & 1 ? ~(result >> 1) : result >> 1;
    shift = 0;
    result = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);

    lng += result & 1 ? ~(result >> 1) : result >> 1;
    // MapLibre GeoJSON standard is [longitude, latitude]
    path.push([lng / 1e5, lat / 1e5]);
  }

  return path.filter(
    (point) => Number.isFinite(point[0]) && Number.isFinite(point[1])
  );
};

// LocalStorage caching for last known GPS coordinates to guarantee instant initial rendering
const COORDS_CACHE_KEY = 'fms_vehicle_coords_cache';

export const getCachedVehicleCoords = () => {
  try {
    const raw = localStorage.getItem(COORDS_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
};

export const saveCachedVehicleCoords = (vehicleList = []) => {
  try {
    if (!Array.isArray(vehicleList) || vehicleList.length === 0) return;
    const currentCache = getCachedVehicleCoords();
    let updated = false;

    vehicleList.forEach((v) => {
      const vId = v.id || v.vehicle_id;
      const vImei = v.imeino || v.imei;
      const vReg = v.reg || v.registration_number;
      const lat = Number(v.lat);
      const lng = Number(v.lng);

      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        const entry = {
          lat,
          lng,
          h: v.h || 0,
          spd: v.spd || 0,
          st: v.st || 'stopped',
          loc: v.loc || '',
        };
        if (vId) currentCache[String(vId)] = entry;
        if (vImei) currentCache[String(vImei)] = entry;
        if (vReg) currentCache[String(vReg).trim().toUpperCase()] = entry;
        updated = true;
      }
    });

    if (updated) {
      localStorage.setItem(COORDS_CACHE_KEY, JSON.stringify(currentCache));
    }
  } catch (_) {}
};

export const mapApiVehicleToTrackingVehicle = (item) => {
  if (!item) return null;

  const rawReg = item.registration_number || item.reg_no || item.vehicle_number || '';
  const reg = rawReg.trim().toUpperCase();

  // Status mapping: "RUNNING" / "MOVING" -> "moving", "IDLE" -> "idle", "STOP" -> "stopped", "INACTIVE" -> "inactive"
  const rawTrackerStatus = String(
    item.tracker_status ||
    item.last_log?.status ||
    item.vehicle_current_detail?.status ||
    item.status ||
    item.current_info?.status ||
    item.vehicle_status ||
    'STOP'
  ).toUpperCase();

  let st = 'stopped';
  if (rawTrackerStatus === 'RUNNING' || rawTrackerStatus === 'MOVING') {
    st = 'moving';
  } else if (rawTrackerStatus === 'IDLE' || rawTrackerStatus === 'RESTING' || rawTrackerStatus === 'LOADING') {
    st = 'idle';
  } else if (rawTrackerStatus === 'INACTIVE' || rawTrackerStatus === 'OFFLINE') {
    st = 'inactive';
  } else if (rawTrackerStatus === 'STOP' || rawTrackerStatus === 'STOPPED') {
    st = 'stopped';
  }

  // Coordinates: support last_log, vehicle_current_detail, current_status, fmcr_trip, current_info, or root fields
  const currentDetail =
    item.vehicle_current_detail ||
    item.current_status ||
    item.current_info ||
    item.last_position ||
    null;

  const lastLogObj = typeof item.last_log === 'object' && item.last_log !== null ? item.last_log : null;
  const tripDetail = item.fmcr_trip?.trip_current_detail || null;

  const rawLat =
    lastLogObj?.lat ??
    lastLogObj?.latitude ??
    currentDetail?.lat ??
    currentDetail?.latitude ??
    tripDetail?.lat ??
    tripDetail?.latitude ??
    item.lat ??
    item.latitude ??
    item.current_lat ??
    item.gps_lat ??
    item.fmcr_trip?.current_lat ??
    item.fmcr_trip?.start_lat ??
    item.fmcr_trip?.start_trip?.latitude ??
    item.start_trip?.latitude ??
    (typeof item.tracker_log === 'object' && item.tracker_log !== null ? (item.tracker_log?.lat ?? item.tracker_log?.latitude) : null);

  const rawLng =
    lastLogObj?.lng ??
    lastLogObj?.longitude ??
    currentDetail?.lng ??
    currentDetail?.longitude ??
    tripDetail?.lng ??
    tripDetail?.longitude ??
    item.lng ??
    item.longitude ??
    item.current_lng ??
    item.gps_lng ??
    item.fmcr_trip?.current_lng ??
    item.fmcr_trip?.start_lng ??
    item.fmcr_trip?.start_trip?.longitude ??
    item.start_trip?.longitude ??
    (typeof item.tracker_log === 'object' && item.tracker_log !== null ? (item.tracker_log?.lng ?? item.tracker_log?.longitude) : null);

  let lat =
    rawLat != null && !isNaN(Number(rawLat)) && Number(rawLat) !== 0
      ? Number(rawLat)
      : null;
  let lng =
    rawLng != null && !isNaN(Number(rawLng)) && Number(rawLng) !== 0
      ? Number(rawLng)
      : null;

  // Fallback to cached last known GPS coordinates if initial payload omitted them
  let cachedHeading = null;
  let cachedLoc = null;
  if ((lat == null || lng == null) && (item.id || item.vehicle_id || item.imeino || reg)) {
    try {
      const cache = getCachedVehicleCoords();
      const cached =
        cache[String(item.id || item.vehicle_id)] ||
        cache[String(item.imeino || item.imei)] ||
        (reg ? cache[reg] : null);
      if (cached && cached.lat != null && cached.lng != null) {
        lat = Number(cached.lat);
        lng = Number(cached.lng);
        if (cached.h) cachedHeading = cached.h;
        if (cached.loc) cachedLoc = cached.loc;
      }
    } catch (_) {}
  }

  // Sanity check: If lat/lng are accidentally inverted (lat > 55 is longitude range for Pakistan)
  if (lat !== null && lng !== null && lat > 55 && lng < 45) {
    const swap = lat;
    lat = lng;
    lng = swap;
  }

  // Speed & ignition
  const spd = Number(
    lastLogObj?.speed ??
    currentDetail?.speed ??
    item.current_info?.speed ??
    item.speed ??
    0
  );

  const ignition =
    lastLogObj?.ignition != null
      ? Boolean(lastLogObj.ignition)
      : currentDetail?.ignition != null
      ? Boolean(currentDetail.ignition)
      : item.ignition != null
      ? Boolean(item.ignition)
      : spd > 0;

  // Driver details
  const drvUsername =
    item.primary_driver?.username?.trim() ||
    item.primary_driver?.name?.trim() ||
    item.driver?.name?.trim() ||
    item.driver_name?.trim() ||
    (typeof item.driver === 'string' ? item.driver.trim() : '') ||
    'No Driver';
  const drvPhone =
    item.primary_driver?.mobile?.trim() ||
    item.primary_driver?.phone?.trim() ||
    item.driver?.mobile?.trim() ||
    item.driver_phone?.trim() ||
    '';
  const drvImage = item.primary_driver?.image || item.driver?.image || null;

  // Vehicle type & category
  const typeTitle =
    (typeof item.vehicle_type === 'string'
      ? item.vehicle_type
      : item.vehicle_type?.title || item.vehicle_type_title) ||
    'Vehicle';
  const typeClass =
    (typeof item.vehicle_type === 'object' && item.vehicle_type !== null
      ? item.vehicle_type?.vehicle_class
      : '') ||
    'commercial';
  const cat =
    typeClass === 'HTV' ||
    /ud-g|fvr|npr|htv|prime.mover|40ft|trailer/i.test(typeTitle)
      ? 'HTV'
      : 'LTV';

  // Active FMCR trip (if vehicle is on trip)
  const fmcrTrip = item.fmcr_trip || null;

  // Extract polyline GeoJSON if available
  let polylineGeoJson = null;
  const polyObj = fmcrTrip?.polyline || item.polyline || null;
  const encPoly =
    (typeof polyObj === 'string' ? polyObj : polyObj?.full_polyline || polyObj?.polyline) ||
    fmcrTrip?.polyline_traveled ||
    item.polyline_traveled ||
    fmcrTrip?.full_polyline ||
    item.full_polyline ||
    '';
  if (encPoly && typeof encPoly === 'string') {
    const decoded = decodePolyline(encPoly);
    if (decoded.length >= 2) polylineGeoJson = decoded;
  } else if (Array.isArray(item.polyline) && item.polyline.length >= 2) {
    polylineGeoJson = item.polyline
      .filter((pt) => Array.isArray(pt) && pt.length >= 2)
      .map(([p0, p1]) => {
        const num0 = Number(p0);
        const num1 = Number(p1);
        if (num0 > 55 && num1 < 45) return [num0, num1]; // [lng, lat]
        if (num1 > 55 && num0 < 45) return [num1, num0]; // [lat, lng] -> [lng, lat]
        return [num1, num0];
      });
  }

  // Heading / Bearing: support angle, heading, bearing, course, direction, or calculate from route
  const rawHeading =
    lastLogObj?.angle ??
    lastLogObj?.heading ??
    lastLogObj?.bearing ??
    currentDetail?.angle ??
    currentDetail?.heading ??
    currentDetail?.bearing ??
    item.angle ??
    item.heading ??
    item.bearing ??
    item.course ??
    item.direction ??
    item.h ??
    null;

  let h = rawHeading != null && !isNaN(Number(rawHeading)) ? Number(rawHeading) : (cachedHeading != null ? Number(cachedHeading) : 0);

  // If heading is 0/missing, derive heading from route polyline or origin->destination
  if (h === 0) {
    if (polylineGeoJson && polylineGeoJson.length >= 2) {
      h = getRouteHeading(lat && lng ? [lng, lat] : null, polylineGeoJson);
    } else {
      const stObj = fmcrTrip?.start_trip || item.start_trip || (Array.isArray(fmcrTrip?.stops) && fmcrTrip.stops[0]) || null;
      const endObj = fmcrTrip?.end_trip || item.end_trip || (Array.isArray(fmcrTrip?.stops) && fmcrTrip.stops[fmcrTrip.stops.length - 1]) || null;
      if (stObj && endObj) {
        const sLng = Number(stObj.longitude || stObj.lng);
        const sLat = Number(stObj.latitude || stObj.lat);
        const eLng = Number(endObj.longitude || endObj.lng);
        const eLat = Number(endObj.latitude || endObj.lat);
        if (!isNaN(sLng) && !isNaN(sLat) && !isNaN(eLng) && !isNaN(eLat) && sLng !== 0 && eLng !== 0) {
          h = computeBearing([sLng, sLat], [eLng, eLat]);
        }
      }
    }
  }

  // Geocoded location or coordinate string
  const loc =
    lastLogObj?.location ||
    currentDetail?.location ||
    item.location ||
    cachedLoc ||
    (lat && lng ? `${lat.toFixed(4)}, ${lng.toFixed(4)}` : 'Awaiting coordinates');

  // Last update timestamp
  const lastTimeRaw =
    lastLogObj?.updated_at ||
    lastLogObj?.recorded_at ||
    lastLogObj?.created_at ||
    currentDetail?.updated_at ||
    currentDetail?.last_seen_at ||
    item.last_seen_at ||
    item.updated_at ||
    item.created_at;
  const last = lastTimeRaw ? new Date(lastTimeRaw).getTime() : Date.now();

  const todayKm = Number(item.today_km ?? 0);
  const todayDriveSec = Number(item.today_driving_seconds ?? 0);
  const todayIdleSec = Number(item.today_idle_seconds ?? 0);
  const odoRef = Number(item.current_info?.odm ?? item.odm ?? 0);

  return {
    id: item.id || item.vehicle_id,
    reg,
    cat,
    type: typeTitle,
    bill: typeTitle,
    dev: item.imeino || item.imei || '',
    imeino: item.imeino || item.imei || '',
    drv: drvUsername,
    phone: drvPhone,
    drvImage,
    cust: fmcrTrip?.customer?.name || item.customer?.name || '',
    cost_center: fmcrTrip?.cost_center?.name || item.cost_center?.name || '',
    st,
    spd,
    h: Math.round(h),
    trip: fmcrTrip,
    stops: fmcrTrip?.stops || [],
    start_trip: fmcrTrip?.start_trip,
    end_trip: fmcrTrip?.end_trip,
    trip_meta_info: fmcrTrip?.trip_meta_info,
    polylineGeoJson,
    odo: odoRef,
    km: todayKm,
    drive: Math.round(todayDriveSec / 60),
    idle: Math.round(todayIdleSec / 60),
    lat,
    lng,
    loc,
    last,
    ignition,
    last_log: item.last_log,
    primary_driver: item.primary_driver,
    vehicle_type: item.vehicle_type,
    lastTrip: item.last_trip
      ? {
          id: item.last_trip.trip_no || item.last_trip.id,
          from: item.last_trip.from || '—',
          to: item.last_trip.to || '—',
          endTs: item.last_trip.ended_at ? new Date(item.last_trip.ended_at).getTime() : null,
          startTs: null,
          total: 0,
          customer: item.last_trip.customer || null,
          idleAsset: false,
        }
      : null,
    r: null,
    t: 0,
    route: polylineGeoJson,
    raw: item,
  };
};

/**
 * Cleanly merges updated view / telemetry data into an existing tracking vehicle
 */
export const mergeVehicleWithViewData = (baseVehicle, viewItem) => {
  if (!baseVehicle || !viewItem) return baseVehicle;
  const mapped = mapApiVehicleToTrackingVehicle({
    ...(baseVehicle.raw || {}),
    ...viewItem,
    primary_driver: viewItem.primary_driver || baseVehicle.primary_driver,
    vehicle_type: viewItem.vehicle_type || baseVehicle.vehicle_type,
  });

  // Calculate dynamic heading if moved
  let mergedH = mapped.h;
  if (!mergedH || mergedH === 0) {
    if (
      mapped.lat != null &&
      baseVehicle.lat != null &&
      (mapped.lat !== baseVehicle.lat || mapped.lng !== baseVehicle.lng)
    ) {
      mergedH = computeBearing([baseVehicle.lng, baseVehicle.lat], [mapped.lng, mapped.lat]);
    } else if (mapped.polylineGeoJson && mapped.polylineGeoJson.length >= 2) {
      mergedH = getRouteHeading(
        [mapped.lng ?? baseVehicle.lng, mapped.lat ?? baseVehicle.lat],
        mapped.polylineGeoJson
      );
    } else {
      mergedH = baseVehicle.h || 0;
    }
  }

  const merged = {
    ...baseVehicle,
    ...mapped,
    // Preserve valid coordinates if new item omitted them
    lat: mapped.lat ?? baseVehicle.lat,
    lng: mapped.lng ?? baseVehicle.lng,
    h: Math.round(mergedH),
    loc: mapped.lat && mapped.lng ? mapped.loc : baseVehicle.loc,
    spd: mapped.spd !== 0 ? mapped.spd : baseVehicle.spd,
    st: mapped.st || baseVehicle.st,
    polylineGeoJson: mapped.polylineGeoJson || baseVehicle.polylineGeoJson,
  };

  if (merged.lat && merged.lng) {
    saveCachedVehicleCoords([merged]);
  }

  return merged;
};

/**
 * Helper to format a Date object as SQL format 'YYYY-MM-DD HH:mm:ss'
 */
export const formatSqlDateTime = (d) => {
  if (!d || isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const h = pad(d.getHours());
  const min = pad(d.getMinutes());
  const s = pad(d.getSeconds());
  return `${y}-${m}-${day} ${h}:${min}:${s}`;
};

/**
 * Generates { from, to } string queries formatted as 'YYYY-MM-DD HH:mm:ss'
 */
export const getHistoryDateRange = (period = 'today', customRange = null) => {
  const now = new Date();

  if (period === '24h') {
    const fromD = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return { from: formatSqlDateTime(fromD), to: formatSqlDateTime(now) };
  }

  if (period === 'today') {
    const fromD = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const toD = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    return { from: formatSqlDateTime(fromD), to: formatSqlDateTime(toD) };
  }

  if (period === 'yesterday') {
    const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const fromD = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 0, 0, 0);
    const toD = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 59);
    return { from: formatSqlDateTime(fromD), to: formatSqlDateTime(toD) };
  }

  if (period === '7d') {
    const d7 = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
    const fromD = new Date(d7.getFullYear(), d7.getMonth(), d7.getDate(), 0, 0, 0);
    const toD = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    return { from: formatSqlDateTime(fromD), to: formatSqlDateTime(toD) };
  }

  if (period === '14d') {
    const d14 = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 14);
    const fromD = new Date(d14.getFullYear(), d14.getMonth(), d14.getDate(), 0, 0, 0);
    const toD = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    return { from: formatSqlDateTime(fromD), to: formatSqlDateTime(toD) };
  }

  if (period === '30d') {
    const d30 = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
    const fromD = new Date(d30.getFullYear(), d30.getMonth(), d30.getDate(), 0, 0, 0);
    const toD = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    return { from: formatSqlDateTime(fromD), to: formatSqlDateTime(toD) };
  }

  if (period === 'custom') {
    let fromStr = customRange?.from || '';
    let toStr = customRange?.to || '';

    if (fromStr) {
      fromStr = fromStr.replace('T', ' ');
      if (fromStr.length === 10) fromStr += ' 00:00:00';
      else if (fromStr.length === 16) fromStr += ':00';
    } else {
      const fromD = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      fromStr = formatSqlDateTime(fromD);
    }

    if (toStr) {
      toStr = toStr.replace('T', ' ');
      if (toStr.length === 10) toStr += ' 23:59:59';
      else if (toStr.length === 16) toStr += ':59';
    } else {
      const toD = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      toStr = formatSqlDateTime(toD);
    }

    return { from: fromStr, to: toStr };
  }

  const fromD = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const toD = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  return { from: formatSqlDateTime(fromD), to: formatSqlDateTime(toD) };
};

/**
 * Parses formatted duration strings (e.g., "24h 31m", "2h 26m", "45m") into minutes
 */
export const parseFormattedDurationToMinutes = (str) => {
  if (!str || typeof str !== 'string') return 0;
  let totalMin = 0;
  const hMatch = str.match(/(\d+)\s*h/i);
  const mMatch = str.match(/(\d+)\s*m/i);
  const sMatch = str.match(/(\d+)\s*s/i);
  if (hMatch) totalMin += parseInt(hMatch[1], 10) * 60;
  if (mMatch) totalMin += parseInt(mMatch[1], 10);
  if (sMatch) totalMin += parseInt(sMatch[1], 10) / 60;
  return totalMin;
};

/**
 * Calculates Great-circle distance between two [lng, lat] points in meters
 */
const calcHavMeters = (a, b) => {
  if (!a || !b) return 0;
  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const s =
    sinLat * sinLat +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * sinLng * sinLng;
  return 2 * R * Math.asin(Math.sqrt(Math.min(1, s)));
};

/**
 * Groups continuous telemetry GPS points into moving, idle, and stopped segments
 */
export const buildSegmentsFromPoints = (pts) => {
  if (!pts || pts.length === 0) return [];
  const getStatus = (q) => (q.spd > 0 ? 'moving' : q.ign ? 'idle' : 'stopped');

  const rawSegs = [];
  pts.forEach((q, n) => {
    const st = getStatus(q);
    const last = rawSegs[rawSegs.length - 1];
    if (last && last.st === st) {
      last.n1 = n;
    } else {
      rawSegs.push({ st, n0: n, n1: n });
    }
  });

  const segs = rawSegs.map((g) => {
    const nEnd = Math.min(pts.length - 1, g.n1 + 1);
    const t0 = pts[g.n0].t;
    const t1 = pts[nEnd].t;
    const min = Math.max(0, (t1 - t0) / 60000);

    let d = 0;
    for (let n = g.n0; n < nEnd; n++) {
      d += calcHavMeters(pts[n].p, pts[n + 1].p);
    }
    const km = d / 1000;

    const sp = pts
      .slice(g.n0, nEnd + 1)
      .map((q) => q.spd)
      .filter((x) => x > 0);
    const max = sp.length ? Math.max(...sp) : 0;
    const avg = sp.length
      ? Math.round(sp.reduce((acc, x) => acc + x, 0) / sp.length)
      : 0;

    return {
      st: g.st,
      n0: g.n0,
      n1: g.n1,
      t0,
      t1,
      min,
      km,
      max,
      avg,
      pEnd: pts[nEnd].p,
      locationStart: pts[g.n0]?.location || '',
      locationEnd: pts[nEnd]?.location || pts[g.n1]?.location || '',
    };
  });

  // Keep meaningful segments
  const meaningful = segs.filter((g) => g.min >= 0.5);
  return meaningful.length > 0 ? meaningful : segs;
};

/**
 * Maps the backend FMCR vehicle history response into tracking history model
 */
export const mapApiVehicleHistoryToHistoryData = (apiData, vehicle = {}) => {
  if (!apiData) return null;

  const data = apiData?.data || apiData || {};
  const summary = data.summary || {};
  const rawPoints = Array.isArray(data.points) ? data.points : [];
  const rawPolyline = Array.isArray(data.polyline) ? data.polyline : [];
  const startPoint = data.start_point || null;
  const endPoint = data.end_point || null;

  // Convert raw API points into UI point objects
  const pts = rawPoints.map((pt, idx) => {
    let lat = Number(pt.lat);
    let lng = Number(pt.lng);
    if (lat > 55 && lng < 45) {
      const tmp = lat;
      lat = lng;
      lng = tmp;
    }
    const spd = Number(pt.speed ?? 0);
    let h = Number(pt.heading ?? pt.angle ?? pt.bearing ?? 0);
    // If heading is 0, infer direction of travel from sequential history points
    if (h === 0 && rawPoints.length >= 2) {
      if (idx < rawPoints.length - 1) {
        const nextPt = rawPoints[idx + 1];
        const nLng = Number(nextPt.lng);
        const nLat = Number(nextPt.lat);
        if (!isNaN(nLng) && !isNaN(nLat)) {
          h = computeBearing([lng, lat], [nLng, nLat]);
        }
      } else if (idx > 0) {
        const prevPt = rawPoints[idx - 1];
        const pLng = Number(prevPt.lng);
        const pLat = Number(prevPt.lat);
        if (!isNaN(pLng) && !isNaN(pLat)) {
          h = computeBearing([pLng, pLat], [lng, lat]);
        }
      }
    }

    const ign = Boolean(pt.ignition);
    const recordedAt = pt.recorded_at;
    const t = recordedAt ? new Date(recordedAt).getTime() : Date.now();
    const loc =
      pt.location ||
      (idx === 0 && startPoint ? startPoint.location : '') ||
      (idx === rawPoints.length - 1 && endPoint ? endPoint.location : '');

    return {
      id: pt.id ?? idx,
      p: [lng, lat], // GeoJSON standard [longitude, latitude]
      lat,
      lng,
      spd,
      h: Math.round(h),
      ign,
      t,
      time: pt.time || '',
      location: loc,
      recorded_at: recordedAt,
      kind: idx === 0 ? 'start' : idx === rawPoints.length - 1 ? 'end' : null,
    };
  });

  // Polyline coords in GeoJSON [lng, lat]
  const polylineCoords = rawPolyline
    .filter((pt) => Array.isArray(pt) && pt.length >= 2)
    .map(([lat, lng]) => {
      let fLat = Number(lat);
      let fLng = Number(lng);
      if (fLat > 55 && fLng < 45) {
        fLat = Number(lng);
        fLng = Number(lat);
      }
      return [fLng, fLat];
    });

  // Fallback points from polyline if points array was empty
  const finalPts =
    pts.length > 0
      ? pts
      : polylineCoords.map((coord, idx) => {
          let h = 0;
          if (idx < polylineCoords.length - 1) {
            h = computeBearing(coord, polylineCoords[idx + 1]);
          } else if (idx > 0) {
            h = computeBearing(polylineCoords[idx - 1], coord);
          }
          return {
            id: idx,
            p: coord,
            lat: coord[1],
            lng: coord[0],
            spd: 0,
            h: Math.round(h),
            ign: false,
            t: Date.now(),
            time: '',
            location:
              idx === 0 && startPoint
                ? startPoint.location
                : idx === polylineCoords.length - 1 && endPoint
                ? endPoint.location
                : '',
          };
        });

  const fromTs = startPoint?.recorded_at
    ? new Date(startPoint.recorded_at).getTime()
    : data.from_time
    ? new Date(data.from_time).getTime()
    : finalPts[0]?.t || Date.now();

  const toTs = endPoint?.recorded_at
    ? new Date(endPoint.recorded_at).getTime()
    : data.to_time
    ? new Date(data.to_time).getTime()
    : finalPts[finalPts.length - 1]?.t || Date.now();

  const totalKm = Number(summary.total_km ?? 0);
  const avgSpeed = Number(summary.avg_speed_kmh ?? 0);
  const maxSpeed = Number(summary.max_speed_kmh ?? 0);
  const estFuel = Number(summary.est_fuel_litres ?? 0);
  const totalPoints = Number(summary.total_points ?? finalPts.length);

  const drivingFormatted = summary.driving_time_formatted || '';
  const idleFormatted = summary.idle_time_formatted || '';
  const stoppedFormatted = summary.stopped_time_formatted || '';
  const totalFormatted = summary.total_time_formatted || '';

  const mv = drivingFormatted ? parseFormattedDurationToMinutes(drivingFormatted) : 0;
  const idle = idleFormatted ? parseFormattedDurationToMinutes(idleFormatted) : 0;
  const stp = stoppedFormatted ? parseFormattedDurationToMinutes(stoppedFormatted) : 0;

  const segs = buildSegmentsFromPoints(finalPts);
  const stoppedSegsCount = segs.filter((g) => g.st === 'stopped').length;

  const qEnd = finalPts[finalPts.length - 1] || {};
  const endSt = qEnd.spd > 0 ? 'moving' : qEnd.ign ? 'idle' : 'stopped';

  const vehicleId = data.vehicle_id || vehicle?.id;
  const reg = (data.registration_number || vehicle?.reg || '').toUpperCase();
  const imeino = data.imeino || vehicle?.imeino || vehicle?.dev || '';
  const vehicleType = data.vehicle_type || vehicle?.type || 'Vehicle';

  const v = {
    ...vehicle,
    id: vehicleId,
    reg,
    dev: imeino,
    imeino,
    type: vehicleType,
    cat:
      vehicle?.cat ||
      (/PORTER|H-100|TRUCK|UD|FAW|HTV/i.test(vehicleType) ? 'HTV' : 'LTV'),
    drv: vehicle?.drv || 'Driver',
  };

  const endCoord =
    endPoint && endPoint.lng && endPoint.lat
      ? [Number(endPoint.lng), Number(endPoint.lat)]
      : finalPts[finalPts.length - 1]?.p || [67.0, 24.8];

  const H = {
    pts: finalPts,
    polylineCoords:
      polylineCoords.length > 0 ? polylineCoords : finalPts.map((q) => q.p),
    dist: totalKm,
    max: maxSpeed,
    avg: avgSpeed,
    from: fromTs,
    to: toTs,
  };

  return {
    v,
    H,
    segs,
    mv,
    idle,
    stp,
    endSt,
    km: totalKm,
    max: maxSpeed,
    avg: avgSpeed,
    fuel: estFuel,
    stops: stoppedSegsCount,
    start: fromTs,
    end: toTs,
    endP: endCoord,
    startPoint,
    endPoint,
    summary: {
      ...summary,
      total_km: totalKm,
      total_points: totalPoints,
      avg_speed_kmh: avgSpeed,
      max_speed_kmh: maxSpeed,
      est_fuel_litres: estFuel,
      idle_time_formatted: idleFormatted,
      total_time_formatted: totalFormatted,
      driving_time_formatted: drivingFormatted,
      stopped_time_formatted: stoppedFormatted,
    },
    trips: vehicle?.trip ? [vehicle.trip] : [],
    custs: vehicle?.cust ? [vehicle.cust] : [],
    runs: [],
    vio: 0,
    vioList: [],
    rawApiData: data,
  };
};

/**
 * Normalizes incoming violation data from socket events into a standard structure
 * for VehicleTracking cards, markers, and KPI strips.
 */
export const normalizeViolation = (data) => {
  if (!data) return null;

  const rawType = String(
    data.violation_type ||
    data.type ||
    data.alert_type ||
    data.name ||
    'overspeed'
  ).toLowerCase();

  let type = 'overspeed';
  if (rawType.includes('speed')) type = 'overspeed';
  else if (rawType.includes('brake') || rawType.includes('braking')) type = 'brake';
  else if (rawType.includes('accel')) type = 'accel';
  else if (rawType.includes('corner')) type = 'corner';
  else if (rawType.includes('idle')) type = 'idle';

  const t =
    data.created_at ||
    data.timestamp ||
    data.t ||
    data.time ||
    data.event_time
      ? new Date(data.created_at || data.timestamp || data.t || data.time || data.event_time).getTime()
      : Date.now();

  const val =
    data.speed != null && !isNaN(Number(data.speed))
      ? Number(data.speed)
      : data.val != null && !isNaN(Number(data.val))
      ? Number(data.val)
      : data.value != null && !isNaN(Number(data.value))
      ? Number(data.value)
      : type === 'overspeed'
      ? 75
      : 1;

  const lim =
    data.speed_limit != null && !isNaN(Number(data.speed_limit))
      ? Number(data.speed_limit)
      : data.lim != null && !isNaN(Number(data.lim))
      ? Number(data.lim)
      : data.limit != null && !isNaN(Number(data.limit))
      ? Number(data.limit)
      : 100;

  const rawSev = String(data.severity || data.sev || (val > lim + 15 ? 'hi' : 'med')).toLowerCase();
  const sev = rawSev === 'high' ? 'hi' : rawSev === 'medium' ? 'med' : rawSev === 'low' ? 'low' : rawSev;

  const vehicleId = data.vehicle_id || data.vehicleId || data.vehicle?.id || null;
  const tripId = data.trip_id || data.tripId || data.trip?.id || null;
  const reg =
    data.vehicle_reg ||
    data.registration_no ||
    data.registration_number ||
    data.vehicle?.registration_number ||
    data.reg ||
    '';
  const imei = data.imeino || data.imei || data.dev || data.device_id || '';
  const loc = data.location || data.loc || data.address || '';
  const message =
    data.message ||
    data.alert ||
    data.description ||
    `${type.toUpperCase()} alert triggered`;

  return {
    ...data,
    id: data.id || `vio-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
    vehicle_id: vehicleId,
    trip_id: tripId,
    reg: reg ? String(reg).toUpperCase() : '',
    imeino: imei ? String(imei).trim() : '',
    type,
    val,
    lim,
    sev,
    t,
    loc,
    message,
  };
};

/**
 * Maps the response from POST /fmcr/vehicle-history-summary into the fleet data model
 * used by VehicleTrackingHistory and VehicleTrackingSummaryStrip.
 */
export const mapApiHistorySummaryToFleetData = (summaryResponse, baseVehicles = []) => {
  const rawList = Array.isArray(summaryResponse?.data?.list)
    ? summaryResponse.data.list
    : Array.isArray(summaryResponse?.list)
    ? summaryResponse.list
    : Array.isArray(summaryResponse?.data)
    ? summaryResponse.data
    : Array.isArray(summaryResponse)
    ? summaryResponse
    : [];

  const baseMap = new Map();
  (baseVehicles || []).forEach((v) => {
    if (v.id) baseMap.set(Number(v.id), v);
    if (v.reg) baseMap.set(String(v.reg).toUpperCase(), v);
  });

  return rawList.map((item) => {
    const rawV = item.vehicle || {};
    const rawD = item.driver || {};
    const vId = Number(rawV.id || item.id);
    const regUpper = String(rawV.registration_number || item.registration_number || '').toUpperCase();
    const base = baseMap.get(vId) || baseMap.get(regUpper) || {};

    const vType = rawV.vehicle_type || base.type || 'Vehicle';
    const isHtv = /truck|trailer|ud|prime|hino|isuzu|heavy|bus|tanker/i.test(vType);
    const cat = isHtv ? 'HTV' : 'LTV';

    const drvName =
      rawD.name && rawD.name.toLowerCase() !== 'unassigned'
        ? rawD.name
        : base.drv || 'Unassigned';
    const drvInitials =
      rawD.initials || (drvName && drvName !== 'Unassigned' ? drvName.slice(0, 2).toUpperCase() : 'UN');
    const drvPhone = rawD.phone || base.phone || '';

    // Trips
    const trips = Array.isArray(item.trips)
      ? item.trips.map((t, tIdx) => ({
          id: t.id,
          trip_no: t.trip_no || (t.id ? `JH-${t.id}` : `Trip #${tIdx + 1}`),
          route: t.route || (t.from && t.to ? `${t.from} → ${t.to}` : t.trip_no || (t.id ? `JH-${t.id}` : `Trip #${tIdx + 1}`)),
          from: t.from || '',
          to: t.to || '',
          status: t.status || 'in_transit',
          st: t.status === 'completed' || t.status === 'done' ? 'done' : 'in_transit',
          customer_name: t.customer_name || null,
        }))
      : base.trip
      ? [base.trip]
      : [];

    // Customers
    const tripCusts = trips
      .map((t) => t.customer_name || (t.from && t.from.includes('(') ? t.from.split('(')[0].trim() : null))
      .filter(Boolean);

    const custs =
      Array.isArray(item.customers_served) && item.customers_served.length > 0
        ? item.customers_served
        : item.customers_served_formatted && item.customers_served_formatted !== 'N/A'
        ? [item.customers_served_formatted]
        : tripCusts.length > 0
        ? Array.from(new Set(tripCusts))
        : base.cust
        ? [base.cust]
        : [];

    const distKm = Number(
      item.distance_km != null ? item.distance_km : parseFloat(item.distance) || 0
    );
    const drivingSec = Number(item.driving_seconds || 0);
    const idleSec = Number(item.idle_seconds || 0);
    const stoppedSec = Number(item.stopped_seconds || 0);

    const mvMin = Math.round(drivingSec / 60);
    const idleMin = Math.round(idleSec / 60);
    const stpMin = Math.round(stoppedSec / 60);

    // End status
    const endSt =
      drivingSec > 0 && stoppedSec === 0
        ? 'moving'
        : idleSec > 0 && stoppedSec === 0
        ? 'idle'
        : 'stopped';

    // Avg speed
    const avgSpd =
      item.avg_speed != null
        ? Math.round(Number(item.avg_speed))
        : drivingSec > 0 && distKm > 0
        ? Math.round(distKm / (drivingSec / 3600))
        : 0;

    const maxSpd = item.max_speed != null ? Math.round(Number(item.max_speed)) : avgSpd;
    const estFuel =
      item.fuel != null ? Number(item.fuel) : distKm > 0 ? Math.round(distKm / 3.5) : 0;

    const vObj = {
      ...base,
      id: vId,
      reg: regUpper,
      type: vType,
      model: rawV.model || base.model || '',
      make: rawV.make || base.make || '',
      cat,
      drv: drvName,
      initials: drvInitials,
      phone: drvPhone,
      st: endSt,
      km: distKm,
      spd: avgSpd,
    };

    return {
      v: vObj,
      km: distKm,
      distanceStr: item.distance || `${distKm} km`,
      mv: mvMin,
      mvStr: item.driving || `${mvMin} min`,
      idle: idleMin,
      idleStr: item.idle || `${idleMin} min`,
      stp: stpMin,
      stpStr: item.stopped || `${stpMin} min`,
      stops: Number(item.stops || 0),
      vio: Number(item.violations || 0),
      vioHi: Number(item.violations || 0) > 2,
      trips,
      custs,
      avg: avgSpd,
      max: maxSpd,
      fuel: estFuel,
      endSt,
      endP: base.lng && base.lat ? [base.lng, base.lat] : [67.0, 24.8],
      runs: [],
      segs: [],
      trips: trips || [],
      custs: custs || [],
      H: {
        from: 0,
        to: 0,
        pts:
          base.lng && base.lat
            ? [{ p: [base.lng, base.lat], t: Date.now(), spd: avgSpd, ign: base.ignition, location: base.loc }]
            : [],
      },
    };
  });
};

